import { useTitles } from "hooks/graphql/useTitles";
import type { RacingHit } from "components/layout/Search/Results/models/racingHit";
import { TitleIcon } from "components/TitleIcon";
import * as styles from "components/layout/Search/Results/Results.module.scss";
import React, { useRef } from "react";
import { ResultsInIndex } from "./ResultsInIndex";
import { Row } from "./Row";
import { Highlight, NextHighlight } from "./Highlight";
import { useNormalisedHits } from "components/layout/Search/Results/hooks/useNormalisedHits";

export const RacingResults = () => {
  const titles = useTitles();
  const { items, results } = useNormalisedHits<RacingHit>();
  const hasMatchedOnRunner = useRef(false);

  return (
    <ResultsInIndex
      indexName={`Racing`}
      total={results?.nbHits || 0}
      pages={results?.nbPages || 0}
      perPage={results?.hitsPerPage || 0}
    >
      {(items || []).map((item) => {
        const venueHighlight = item.normalisedHighlights?.venue?.[0];

        const titleHighlightPath = "venue.0";

        // default second highlight to event name
        let secondaryHighlightPath = "event.0";
        let secondaryHighlightText = item.event;
        let secondaryHighlight = item.normalisedHighlights?.event?.[0];

        const additionalHighlights = [];
        let ignorePaths = [titleHighlightPath, secondaryHighlightPath];

        if (
          ["HORSE_RACING", "HARNESS_RACING", "GREYHOUNDS"].includes(item.sport)
        ) {
          // we want to replace the second highlight with the competitor instead of the event name if its matched competitor, jockey or trainer on the search terms
          const competitorMatchIndex =
            item.normalisedHighlights?.competitors?.findIndex(
              ({ matchedWords }) => matchedWords.length,
            ) ?? -1;
          const jockeysMatchIndex =
            item.normalisedHighlights?.jockeys?.findIndex(
              ({ matchedWords }) => matchedWords.length,
            ) ?? -1;
          const trainersMatchIndex =
            item.normalisedHighlights?.trainers?.findIndex(
              ({ matchedWords }) => matchedWords.length,
            ) ?? -1;

          const matchIndex =
            [competitorMatchIndex, jockeysMatchIndex, trainersMatchIndex].find(
              (index) => index !== -1,
            ) ?? -1;

          if (matchIndex !== -1) {
            hasMatchedOnRunner.current = true;

            secondaryHighlightPath = `competitors.${matchIndex}`;
            secondaryHighlightText = item.competitors[matchIndex];
            secondaryHighlight =
              item.normalisedHighlights?.competitors?.[matchIndex];

            ignorePaths = [titleHighlightPath, secondaryHighlightPath];

            // we want to also show the jockey/driver and trainer if applicable
            if (item.jockeys[matchIndex]) {
              // Ignore all jockeys in the next highlight (down further)
              // don't just ignore the matched jockey.index, there may be a partial match on another runners jockey
              ignorePaths.push(`jockeys`);
              const title = item.sport === "HARNESS_RACING" ? "D: " : "J: ";
              additionalHighlights.push(
                <div
                  key={`additional-highlight-jockey`}
                  className={styles.additionalHighlightRow}
                >
                  {title}
                  <Highlight
                    text={item.jockeys[matchIndex]}
                    matchedWords={
                      item.normalisedHighlights?.jockeys?.[matchIndex]
                        ?.matchedWords || []
                    }
                    html={
                      item.normalisedHighlights?.jockeys?.[matchIndex]?.value ||
                      ""
                    }
                    className={styles.displayInlineBlock}
                  />
                </div>,
              );
            }

            if (item.trainers[matchIndex]) {
              // Ignore all trainers in the next highlight (down further)
              // don't just ignore the matched trainer.index, there may be 2 runners with the same trainer
              ignorePaths.push(`trainers`);
              additionalHighlights.push(
                <div
                  key={`additional-highlight-trainer`}
                  className={styles.additionalHighlightRow}
                >
                  T:{" "}
                  <Highlight
                    text={item.trainers[matchIndex]}
                    matchedWords={
                      item.normalisedHighlights?.trainers?.[matchIndex]
                        ?.matchedWords || []
                    }
                    html={
                      item.normalisedHighlights?.trainers?.[matchIndex]
                        ?.value || ""
                    }
                    className={styles.displayInlineBlock}
                  />
                </div>,
              );
            }
          }
        }

        return (
          <Row
            key={item.objectID}
            eventId={item.objectID}
            icon={<TitleIcon title={titles[item.sport?.toLowerCase()]} svg />}
            title={
              <>
                <div className={styles.raceNumber}>R{item.raceNumber}</div>
                <Highlight
                  text={item.venue}
                  matchedWords={venueHighlight?.matchedWords || []}
                  html={venueHighlight?.value || ""}
                />
              </>
            }
            subTitle={
              <>
                <div>
                  <Highlight
                    text={secondaryHighlightText}
                    matchedWords={secondaryHighlight?.matchedWords || []}
                    html={secondaryHighlight?.value || ""}
                    convertToTitleCase
                  />
                </div>
                <div>{additionalHighlights}</div>
              </>
            }
            to={`/racing/betting/race/${item.meetingId}/${item.objectID}`}
            startTime={item.scheduledStartTime}
            {...(!hasMatchedOnRunner.current && {
              highlight: (
                <NextHighlight
                  highlights={item.normalisedHighlights}
                  ignore={ignorePaths}
                  sport={item.sport}
                />
              ),
            })}
          />
        );
      })}
    </ResultsInIndex>
  );
};
